import type { Money, Timestamp } from '@/types/domain';

/**
 * Формат денег: 125000 → "1 250 ₽"
 * Деньги храним в копейках, отображаем в рублях.
 */
export const formatMoney = (amount: Money, currency: string = 'RUB'): string => {
  const value = amount / 100;
  const symbol = currency === 'RUB' ? '₽' : currency;
  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
  return `${formatted} ${symbol}`;
};

/**
 * Время до окончания аукциона: "2 дн 14:32:05" или "00:00:12"
 * Возвращает раздельно для разной верстки.
 */
export interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isOver: boolean;
  totalMs: number;
}

export const getTimeLeft = (endsAt: Timestamp): TimeLeft => {
  const end = new Date(endsAt).getTime();
  const now = Date.now();
  const diff = Math.max(0, end - now);

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return {
    days,
    hours,
    minutes,
    seconds,
    isOver: diff === 0,
    totalMs: diff,
  };
};

/**
 * Компактный формат: для карточек в ленте
 * "2д 14ч" / "14:32" / "32 мин" / "Скоро"
 */
export const formatTimeLeftCompact = (endsAt: Timestamp): string => {
  const t = getTimeLeft(endsAt);
  if (t.isOver) return 'Завершён';
  if (t.days > 0) return `${t.days}д ${t.hours}ч`;
  if (t.hours > 0) return `${pad2(t.hours)}:${pad2(t.minutes)}:${pad2(t.seconds)}`;
  if (t.minutes > 0) return `${t.minutes} мин`;
  return `${t.seconds} сек`;
};

/**
 * Полный формат: для детальной страницы (моноширинный таймер)
 * "02:14:32:05" (дни:часы:минуты:секунды)
 */
export const formatTimeLeftFull = (endsAt: Timestamp): string => {
  const t = getTimeLeft(endsAt);
  if (t.isOver) return 'Завершён';
  return `${pad2(t.days)}:${pad2(t.hours)}:${pad2(t.minutes)}:${pad2(t.seconds)}`;
};

/** Можно ли ещё ставить: active и ends_at ещё не наступил. */
export const isAuctionTradingOpen = (auction: {
  status: string;
  ends_at: Timestamp;
}): boolean => {
  if (auction.status !== 'active') return false;
  return !getTimeLeft(auction.ends_at).isOver;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * "5 минут назад", "вчера", "12 мар"
 */
export const formatRelativeTime = (timestamp: Timestamp): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffH = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин назад`;
  if (diffH < 24) return `${diffH} ч назад`;
  if (diffDays === 1) return 'вчера';
  if (diffDays < 7) return `${diffDays} дн назад`;

  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' }).format(date);
};

/**
 * Состояние товара → читаемое
 */
export const formatCondition = (condition: string): string => {
  const map: Record<string, string> = {
    new: 'Идеальное (10/10)',
    like_new: 'Как новое',
    good: 'Хорошее',
    used: 'Б/у',
    damaged: 'С дефектом коробки',
    no_box: 'Без коробки',
    damaged_figure: 'С дефектом фигурки',
  };
  return map[condition] ?? condition;
};
